import { produce } from 'immer';
/** Represents the prefix for the group type. */
export const groupTypePrefix = 'group:';
/** Represents the mappings from event type to event group. */
const eventGroupConfig = {
    'conversation:conversational_assistant_message': {
        groupType: 'group:conversation:conversational_assistant',
    },
    'conversation:rating_cancelled': {
        groupType: 'group:conversation:rating',
    },
    'conversation:rating_scheduled': {
        groupType: 'group:conversation:rating',
    },
    'conversation:rating_unscheduled': {
        groupType: 'group:conversation:rating',
    },
    'conversation:transfer_answered': {
        groupType: 'group:conversation:transfer',
    },
    'conversation:transfer_initiated': {
        groupType: 'group:conversation:transfer',
    },
    'conversation:transfer_status': {
        groupType: 'group:conversation:transfer',
    },
    'conversation:message_added': {
        groupType: (event, mainConversation) => {
            // Because both instagram and facebook messenger are under the same channel,
            // we have to differentiate on the group level so it's easier to work with.
            // Because instagram stories are visually different they get their own
            // group.
            if (mainConversation.initial_channel === 'facebook_messenger' &&
                mainConversation.attributes.instagram_profile_id &&
                event.channel === 'facebook_messenger' &&
                typeof event.is_story === 'boolean') {
                return 'group:instagram_story';
            }
            return `${groupTypePrefix}${event.channel}`;
        },
        merge: (groupType, currentEvent, previousEvent) => {
            const isSameAuthor = currentEvent.author_id === previousEvent.author_id;
            const isSameDirection = currentEvent.direction === previousEvent.direction;
            const isRecent = new Date(currentEvent.created_at).getTime() -
                new Date(previousEvent.created_at).getTime() <=
                30000;
            /**
             * We do not group instagram story events as their visual representation
             * differ from regular messages and should therefore be viewed in
             * isolation.
             */
            const isInstagramStory = groupType === 'group:instagram_story' &&
                currentEvent.channel === 'facebook_messenger' &&
                typeof currentEvent.is_story === 'boolean';
            return isSameAuthor && isSameDirection && isRecent && !isInstagramStory;
        },
    },
};
/**
 * Groups the events based on their type and grouping conditions.
 *
 * @param events The conversation events.
 * @param eventGroupTypes The event group types to include in the result.
 */
export function getConversationEventGroups(mainConversation, eventGroupTypes = []) {
    return mainConversation.events
        .filter((event) => eventGroupTypes.includes(getEventType(event, mainConversation)))
        .reduce((eventGroups, currentEvent, index, events) => produce(eventGroups, (draft) => {
        var _a;
        const previousEvent = events[index - 1];
        const previousEventGroupType = previousEvent && getEventType(previousEvent, mainConversation);
        const currentEventGroupType = getEventType(currentEvent, mainConversation);
        if (currentEventGroupType === previousEventGroupType &&
            shouldBeGroupedWithPreviousEvent(currentEventGroupType, currentEvent, previousEvent)) {
            ((_a = draft[draft.length - 1]) === null || _a === void 0 ? void 0 : _a.events).push(currentEvent);
        }
        else {
            draft.push({
                type: currentEventGroupType,
                events: [currentEvent],
            });
        }
    }), []);
}
function getEventType(event, mainConversation) {
    var _a;
    const config = eventGroupConfig[event.type];
    if (config && typeof config !== 'boolean') {
        if (typeof config.groupType === 'string') {
            return config.groupType;
        }
        return (_a = config.groupType) === null || _a === void 0 ? void 0 : _a.call(config, event, mainConversation);
    }
    return `${groupTypePrefix}${event.type}`;
}
function shouldBeGroupedWithPreviousEvent(currentEventGroupType, currentEvent, previousEvent) {
    if (!previousEvent) {
        return false;
    }
    const config = eventGroupConfig[currentEvent.type];
    if (typeof config === 'undefined') {
        return false;
    }
    if (typeof config === 'boolean') {
        return config;
    }
    if (config.merge) {
        return config.merge(currentEventGroupType, currentEvent, previousEvent);
    }
    return true;
}
